import type { GetRemoteStringFunction } from '@acorns/web-utils'

import {
  Employees,
  EmploymentStatus,
  FilingType,
  FinancialEducation,
  InvestmentGoal,
  MaritalStatus,
} from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { FlowRoute, Flow } from 'utils/FlowProvider/types'

interface Input {
  pathname: FlowRoute
  stringPrefix: string
  getRemoteString: GetRemoteStringFunction
  flow: Flow
}

const INVESTMENT_GOALS = [
  InvestmentGoal.EXPONENTIAL_GROWTH,
  InvestmentGoal.MODERATE_GROWTH,
  InvestmentGoal.BALANCED,
  InvestmentGoal.CONSERVATIVE,
  InvestmentGoal.REDUCED_RISK,
]

const FINANCIAL_EDUCATION = [
  FinancialEducation.BEGINNER,
  FinancialEducation.MODERATE,
  FinancialEducation.EXPERT,
]

const MARITAL_STATUS = [
  MaritalStatus.single,
  MaritalStatus.married,
  MaritalStatus.divorced,
  MaritalStatus.widowed,
]

const TAX_FILING_TYPE = [
  FilingType.joint,
  FilingType.separatePartYear,
  FilingType.separateWholeYear,
]

const PATH_TO_ORDERED_ANSWERS = {
  [FlowRoute.EmploymentStatus]: Object.values(EmploymentStatus),
  [FlowRoute.FinancialEducation]: FINANCIAL_EDUCATION,
  [FlowRoute.InvestmentGoal]: INVESTMENT_GOALS,
  [FlowRoute.MaritalStatus]: MARITAL_STATUS,
  [FlowRoute.TaxFilingType]: TAX_FILING_TYPE,
  [FlowRoute.Employees]: Object.values(Employees),
}

export const getAvailableAnswers = (input: Input) => {
  if (
    input.pathname === FlowRoute.AnnualIncome ||
    input.pathname === FlowRoute.CombinedAnnualIncome ||
    input.pathname === FlowRoute.SelfEmployedIncome
  )
    return null

  if (input.pathname === FlowRoute.NetWorth) {
    const optionAsStr = input.getRemoteString({
      key: 'registration.suitability.answer.net_worth.text',
    })
    return optionAsStr.split(';').map((option) => ({
      title: option,
      id: option,
      testId: TestIDs.SUITABILITY_QUESTIONS.ANSWER,
    }))
  }

  const answers = PATH_TO_ORDERED_ANSWERS[input.pathname]
  return answers.map((option) => ({
    title: input.getRemoteString({
      key: `registration.suitability.answer.${input.stringPrefix}.${option.toLowerCase()}.${option === MaritalStatus.married ? 'alt_' : ''}text`,
    }),
    bodyText:
      input.getRemoteString({
        key: `registration.suitability.answer.${input.stringPrefix}.${option.toLowerCase()}.description`,
      }) || null,
    id: option,
    testId: `${TestIDs.SUITABILITY_QUESTIONS.ANSWER}_${option}`,
  }))
}
